import { DynamicDisplayTargets } from '@/types'
import type { EcomLocation } from '@/types/widget'
import { logger } from './logger'
import { UrlMatcher } from './urlMatcher'

/**
 * Check if the current URL matches the target rules (include/exclude).
 *
 * @param currentUrl The URL to check against.
 * @param targets The targeting rules (include/exclude patterns).
 * @returns true if the URL matches the targets, false otherwise.
 */
export function checkUrlTargets(currentUrl: string, targets?: DynamicDisplayTargets): boolean {
  const { includeURLs = [], excludeURLs = [] } = targets ?? {}

  // Check exclude URLs
  for (const target of excludeURLs) {
    if (!target.condition || !target.value) continue
    if (UrlMatcher.matchesTarget(currentUrl, target)) {
      logger.error('Target check failed (exclude URL condition met)', {
        currentUrl,
        target,
      })
      return false
    }
  }

  // Check include URLs
  // If no include URLs are specified, we assume all URLs are allowed (unless excluded)
  let includeConditionsMet = includeURLs.length === 0
  for (const target of includeURLs) {
    if (UrlMatcher.matchesTarget(currentUrl, target)) {
      includeConditionsMet = true
      break
    }
  }

  if (!includeConditionsMet) {
    logger.error('Target check failed (include URL conditions not met)', {
      currentUrl,
      includeURLs,
    })
    return false
  }

  return true
}

/**
 * Check if a URL is allowed by any ecom location's SubEcomLocation targets.
 *
 * Used as a global gate: if no SubEcomLocation targets permit this URL,
 * the widget overlay should not render on this page.
 *
 * Returns true (allowed) when no locations have targets configured at all,
 * preserving backward compatibility.
 */
export function isUrlAllowedByEcomTargets(currentUrl: string, ecomLocations: EcomLocation[]): boolean {
  const allTargets = ecomLocations
    .flatMap((loc) => loc.subEcomLocations)
    .map((sub) => sub.dynamicDisplayTargets)
    .filter(Boolean)

  if (allTargets.length === 0) return true

  return allTargets.some((targets) => checkUrlTargets(currentUrl, targets))
}
